import {
  type ComputeReturnFeesBody,
  type ComputeReturnFeesResponse,
  usePublicComputeReturnFees,
} from "@babackapp/private-api-client/client/use-public-compute-return-fees";
import { useDraftReturn } from "../hooks/use-draft-return";
import { usePublicShop } from "./use-public-shop";
import { useMemo } from "react";

export type { ComputeReturnFeesResponse };

/**
 * Fetches per-resolution hypothetical return fees for the current draft.
 *
 * The current in-progress line item (identified by `pendingLineItemId` and
 * `pendingReasonId`) is merged into the request so override rules evaluate
 * as soon as the customer picks a reason — before committing a resolution.
 */
export function useComputeReturnFees({
  orderId,
  shopId,
  pendingLineItemId,
  pendingReasonId,
}: {
  orderId: string;
  shopId: string;
  /** Line item currently being configured (merged with placeholder resolution). */
  pendingLineItemId?: string;
  /** Reason picked for the pending line item. */
  pendingReasonId?: string;
}) {
  const { data: shop } = usePublicShop(shopId);
  const draft = useDraftReturn(orderId);

  const providerId = shop?.providerId ?? null;
  const isReturnToStore = draft?.returnMode === "store";

  const body = useMemo((): ComputeReturnFeesBody | null => {
    if (!providerId || !orderId) return null;
    if (!draft?.lineItems.length && !pendingLineItemId) return null;

    const base = (draft?.lineItems ?? []).map((li) => ({
      lineItemId: li.lineItemId,
      quantity: li.quantity,
      reasonId: li.reasonId ?? undefined,
      resolution: li.resolution ?? "refund",
    }));

    // Merge the pending item so fees react as soon as a reason is picked.
    if (pendingLineItemId && pendingReasonId) {
      const entry = {
        lineItemId: pendingLineItemId,
        quantity: 1,
        reasonId: pendingReasonId,
        resolution: "refund" as const, // placeholder — byResolution replaces per-resolution anyway
      };
      const idx = base.findIndex((b) => b.lineItemId === pendingLineItemId);
      if (idx >= 0) base[idx] = entry;
      else base.push(entry);
    }

    if (!base.length) return null;

    return { providerId, orderId, lineItems: base, isReturnToStore };
  }, [providerId, orderId, draft?.lineItems, pendingLineItemId, pendingReasonId, isReturnToStore]);

  return usePublicComputeReturnFees(body);
}
